// Deterministic per-day labels for the DomainDetail Check-in Log.
//
// The previous implementation rendered each row's note as the friendly
// version of that day's ``coherence_status`` enum, which meant every
// FRAGMENTED day read "Finding your rhythm" — a useless per-day
// differentiator. These rules instead derive a short factual phrase
// from the numeric history already in the GET /history payload (no
// model calls, no backend change).
//
// Each rule is evaluated in priority order; the first that fires
// wins. Higher-priority rules describe stronger, more distinctive
// signals — the bottom of the ladder is the band-only fallback.

import { DOMAIN_WORD } from "./domainLabels";
import { PATIENT, lintText } from "./insightRubric";

const SCORE_KEY = {
  BIO: "bio_score",
  PSYCH: "psych_score",
  BEH: "beh_score",
};

// Cross-band threshold — anything below 0.50 reads as "below baseline"
// for label purposes. Mirrors the CLAUDE.md domain-threshold table
// (≥0.75 Positive · 0.50–0.74 Neutral · <0.50 Negative). Kept here
// rather than imported so this util has no app-state dependency.
const BASELINE = 0.50;
const STRONG = 0.75;

// Minimum delta between consecutive days to register as a "real" move.
// 0.02 on a 0.0–1.0 scale (= 2 score points out of 100) is the noise
// floor — anything tighter shouldn't read as a direction change.
const DAY_DELTA_NOISE = 0.02;

// Cross-domain gap threshold for "leading / trailing today's signal".
// Matches the inter-domain gap threshold used by the reveal-insight
// pattern detector — keeps the two narratives in agreement.
const CROSS_DOMAIN_GAP = 0.10;

// Outlier threshold: a day only earns "highest/lowest of the week" if
// it's at least this far from the window mean. Without this, a flat
// week of 0.60s would tag whichever day was 0.601 as "highest of the
// week," which is technically true but reads as noise.
const OUTLIER_GAP = 0.05;


function _series(snapshots, domain) {
  const key = SCORE_KEY[domain];
  return snapshots.map(s => (typeof s[key] === "number" ? s[key] : 0));
}

function _avg(xs) {
  if (!xs.length) return 0;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

function _otherDomains(domain) {
  return ["BIO", "PSYCH", "BEH"].filter(d => d !== domain);
}


// Rubric guard at the boundary: the templates below are deterministic
// and rubric-clean by construction, but a future template edit that
// trips the shared patient lint returns "" (the caller keeps a static
// placeholder for the empty case) instead of shipping the violation.
export function dayLabel(idx, snapshots, domain) {
  const label = _dayLabel(idx, snapshots, domain);
  return label && lintText(label, PATIENT).length ? "" : label;
}

function _dayLabel(idx, snapshots, domain) {
  // Defensive: empty or malformed input gets an empty string; the
  // caller (DomainDetail) already keeps a static placeholder ready
  // for the no-history case.
  if (!Array.isArray(snapshots) || !snapshots.length) return "";
  if (idx < 0 || idx >= snapshots.length) return "";
  if (!SCORE_KEY[domain]) return "";

  const series = _series(snapshots, domain);
  const today = series[idx];
  // Canonical display word ("Body", "Mind", "Behavior") — the screen is
  // already scoped to one domain, but a 3-word phrase reads better than
  // a 1-word phrase in a list row, so the subject stays on the front.
  const subject = DOMAIN_WORD[domain];
  const N = series.length;

  // ── Rules requiring at least one prior day ──────────────────────────
  //
  // Priority order, most-specific-narrative first:
  //   1. Cross-baseline move (threshold crossing — strongest inflection)
  //   2. ≥3-day streak (multi-day arc beats single-day snapshot)
  //   3. Outlier in window (single-day high/low)
  //   4. Cross-domain lead/lag (relative against the other two domains)
  //   5. Day-over-day delta (single-day move)
  //   6. Holding steady (no movement)
  // Anything past 6 falls through to the band-only fallback below.
  if (idx >= 1) {
    const yesterday = series[idx - 1];
    const delta = today - yesterday;

    // Rule 1: cross-baseline move from yesterday
    if (yesterday < BASELINE && today >= BASELINE) {
      return `${subject} back above baseline`;
    }
    if (yesterday >= BASELINE && today < BASELINE) {
      return `${subject} slipped below baseline`;
    }

    // Rule 2: ≥3-day climb / slide
    if (idx >= 2) {
      let ups = 0;
      for (let k = idx; k > 0 && series[k] - series[k - 1] >= DAY_DELTA_NOISE; k--) {
        ups++;
      }
      if (ups >= 3) return `${subject} climbing ${ups} days running`;

      let downs = 0;
      for (let k = idx; k > 0 && series[k - 1] - series[k] >= DAY_DELTA_NOISE; k--) {
        downs++;
      }
      if (downs >= 3) return `${subject} sliding ${downs} days running`;
    }

    // Rule 3: outlier in window (single-day high / low).
    // Requires ≥3 days of context so a 2-row window doesn't tag both
    // rows as outliers of each other.
    if (N >= 3) {
      const mean = _avg(series);
      const max = Math.max(...series);
      const min = Math.min(...series);
      if (today === max && max - mean >= OUTLIER_GAP) {
        return `${subject}'s strongest day this week`;
      }
      if (today === min && mean - min >= OUTLIER_GAP) {
        return `${subject}'s lowest day this week`;
      }
    }

    // Rule 4: leading / lagging across domains today
    const todaySnap = snapshots[idx];
    const otherScores = _otherDomains(domain).map(
      d => todaySnap[SCORE_KEY[d]] || 0
    );
    if (otherScores.length === 2) {
      if (today - Math.max(...otherScores) >= CROSS_DOMAIN_GAP) {
        return `${subject} leading today`;
      }
      if (Math.min(...otherScores) - today >= CROSS_DOMAIN_GAP) {
        return `${subject} trailing the other domains`;
      }
    }

    // Rule 5: up / down from yesterday by ≥0.05
    if (delta >= 0.05) return `${subject} up from yesterday`;
    if (delta <= -0.05) return `${subject} down from yesterday`;

    // Rule 6: holding steady (gap below noise floor)
    if (Math.abs(delta) < DAY_DELTA_NOISE) {
      return `${subject} holding steady`;
    }
  } else if (N >= 3) {
    // First day in the window but the window itself is ≥3 days long
    // (the user is scrolled back past the start of the recent window).
    // Outlier rule can still meaningfully fire here.
    const mean = _avg(series);
    const max = Math.max(...series);
    const min = Math.min(...series);
    if (today === max && max - mean >= OUTLIER_GAP) {
      return `${subject}'s strongest day this week`;
    }
    if (today === min && mean - min >= OUTLIER_GAP) {
      return `${subject}'s lowest day this week`;
    }
  }

  // ── Fallback: band-only label ───────────────────────────────────────
  // First day in the window gets opening-phrasing; later days that
  // didn't match any rule above get a generic band phrase. The
  // first/non-first split is purely tonal — "Mind started clear"
  // reads better at idx 0 than "Mind running clear."
  if (idx === 0) {
    if (today >= STRONG) return `${subject} started strong`;
    if (today >= BASELINE) return `${subject} started mid-range`;
    return `${subject} started below baseline`;
  }
  if (today >= STRONG) return `${subject} running strong`;
  if (today >= BASELINE) return `${subject} in the middle band`;
  return `${subject} running below baseline`;
}
